import { ref } from 'vue'
import { openDB, type IDBPDatabase } from 'idb'

// In-app console mirror. Release builds don't forward the WebView console to
// logcat (Capacitor loggingBehavior defaults to debug-only), so this captures the
// last N console lines into a reactive ring buffer, persisted to IndexedDB so they
// survive the webview reload on login. Surfaced on the /test page.
export type DebugLogLevel = 'log' | 'info' | 'warn' | 'error'
export type DebugLogEntry = { ts: number; level: DebugLogLevel; msg: string }

const MAX_ENTRIES = 100
const DB_NAME = 'atsume-debug-log'
const STORE = 'entries'
const KEY = 'buffer'

export const debugLogEntries = ref<DebugLogEntry[]>([])

let dbPromise: Promise<IDBPDatabase> | null = null
function getDb(): Promise<IDBPDatabase> {
  if (!dbPromise) {
    dbPromise = openDB(DB_NAME, 1, {
      upgrade(db) {
        db.createObjectStore(STORE)
      },
    })
  }
  return dbPromise
}

let persistScheduled = false
function schedulePersist(): void {
  if (persistScheduled) return
  persistScheduled = true
  setTimeout(() => {
    persistScheduled = false
    void getDb()
      .then((db) => db.put(STORE, [...debugLogEntries.value], KEY))
      .catch(() => {})
  }, 500)
}

function formatArg(arg: unknown): string {
  if (typeof arg === 'string') return arg
  if (arg instanceof Error) return `${arg.name}: ${arg.message}`
  try {
    return JSON.stringify(arg)
  } catch {
    return String(arg)
  }
}

function record(level: DebugLogLevel, args: unknown[]): void {
  debugLogEntries.value.push({ ts: Date.now(), level, msg: args.map(formatArg).join(' ') })
  const overflow = debugLogEntries.value.length - MAX_ENTRIES
  if (overflow > 0) debugLogEntries.value.splice(0, overflow)
  schedulePersist()
}

let installed = false
export function installDebugLog(): void {
  if (installed) return
  installed = true

  void getDb()
    .then((db) => db.get(STORE, KEY))
    .then((saved) => {
      if (Array.isArray(saved)) {
        debugLogEntries.value = [...(saved as DebugLogEntry[]), ...debugLogEntries.value].slice(
          -MAX_ENTRIES,
        )
      }
    })
    .catch(() => {})

  const levels: DebugLogLevel[] = ['log', 'info', 'warn', 'error']
  for (const level of levels) {
    const original = console[level].bind(console)
    console[level] = (...args: unknown[]) => {
      record(level, args)
      original(...args)
    }
  }
}

export async function clearDebugLog(): Promise<void> {
  debugLogEntries.value = []
  try {
    const db = await getDb()
    await db.delete(STORE, KEY)
  } catch {
    // Store never created — nothing to clear.
  }
}
